import {type ColorName, colorNames, isColorName, useNamedColor} from '@github-ui/use-named-color'
import {VisuallyHidden} from '@github-ui/visually-hidden'
import {CheckCircleFillIcon, CircleIcon} from '@primer/octicons-react'
import {Box, Radio, Text, Tooltip} from '@primer/react'
import {type ChangeEventHandler, useId} from 'react'

interface ColorPickerProps {
  value: ColorName
  onChange: (value: ColorName) => void
  label: React.ReactNode
}

export const ColorPicker = ({value, onChange, label}: ColorPickerProps) => {
  const name = useId()

  const handleChange: ChangeEventHandler<HTMLInputElement> = event => {
    const newValue = event.target.value
    if (isColorName(newValue)) onChange(newValue)
  }

  return (
    <Box as="fieldset" sx={{border: 0, padding: 0, margin: 0}}>
      <Text as="legend" sx={{fontWeight: 'bold', mb: 2}}>
        {label}
      </Text>

      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
        {colorNames.map(color => (
          <ColorOption key={color} name={name} color={color} onChange={handleChange} selected={value === color} />
        ))}
      </Box>
    </Box>
  )
}

interface ColorOptionProps {
  name: string
  color: ColorName
  onChange: ChangeEventHandler<HTMLInputElement>
  selected: boolean
}

const ColorOption = ({color, onChange, selected, name}: ColorOptionProps) => {
  const {fg, bg} = useNamedColor(color)
  const capitalizedColor = (color[0] ?? '') + color.slice(1).toLowerCase()

  return (
    // eslint-disable-next-line github/a11y-role-supports-aria-props
    <Tooltip role="presentation" aria-label={capitalizedColor} direction="s">
      <Box
        as="label"
        sx={{
          p: 2,
          background: selected ? fg : bg,
          color: selected ? 'canvas.default' : fg,
          borderRadius: 2,
          lineHeight: 0,
          // :has is not yet supported in all browsers, but this isn't critical - it's just
          // to improve accessibility slightly. We can be OK with progressive enhancement as
          // browsers ship support for :has.
          ':has(:focus-visible)': {
            outlineWidth: '2px',
            outlineStyle: 'solid',
            outlineColor: 'accent.fg',
            outlineOffset: '1px',
          },
        }}
      >
        <Radio
          name={name}
          value={color}
          checked={selected}
          onChange={onChange}
          sx={{clipPath: 'circle(0)', position: 'absolute', outline: 'none !important'}}
        />
        <VisuallyHidden>
          {capitalizedColor /* Don't use ALL-CAPS or screen reader might read it letter-by-letter like an acronym */}
        </VisuallyHidden>

        {selected ? <CheckCircleFillIcon /> : <CircleIcon />}
      </Box>
    </Tooltip>
  )
}

try{ ColorPicker.displayName ||= 'ColorPicker' } catch {}
try{ ColorOption.displayName ||= 'ColorOption' } catch {}